// src/api/documents/documents.ts
import { useApiClient } from "../apiClients";
import { DOCUMENTS_ENDPOINTS } from "./endpoints";
import type {
  ChapterField,
  Document,
  DocumentActivation,
  DocumentChapterPatch,
  DocumentVersion,
  DocumentListItem,
  DocumentReview,
  ReviewDecisionPayload,
  ReviewStatus,
} from "../../models/domain";

/** Le PUT réécrit toutes les colonnes du document, jamais celles de chapitre (`patchDocumentChapter`). */
export type UpdateDocumentPayload = Partial<
  Omit<Document, "id" | "created_at" | "updated_at" | ChapterField>
>;

/** Filtres de `GET /v1/documents` (contrat du chantier filières). */
export interface ListDocumentsParams {
  /** Obligatoire : le serveur répond 400 sans lui. */
  program_id: string;
  stage_id?: string;
  grade_id?: string;
  subject_id?: string;
  /** Documents de cette filière + documents communs. */
  track_id?: string;
  /** `program` : semestre, ordre du chapitre, puis date d'import. */
  order?: "program";
  /** États de relecture, séparés par des virgules (ex. `a_relire,a_corriger`). */
  review_status?: ReviewStatus | string;
  /** 50 par défaut côté serveur. */
  limit?: number;
  offset?: number;
  [key: string]: unknown;
}

export type ChapterSourcePart = "markdown" | "manifest";

export const useDocumentsApi = () => {
  const apiClient = useApiClient("embed");

  // tc_embed_be répond `{"items": [...]}` (document_handlers.go) : le dépliage se
  // fait ICI, une fois. Typé en tableau nu, l'enveloppe avait été dépliée à la
  // main par deux écrans et oubliée par le troisième (Essais : liste vide muette).
  const listDocuments = (params: ListDocumentsParams) =>
    apiClient
      .get<{ items: DocumentListItem[] | null }>(DOCUMENTS_ENDPOINTS.BASE, { params })
      .then((r) => ({ ...r, data: r.data?.items ?? [] }));

  const getDocument = (documentId: string) =>
    apiClient.get<Document>(DOCUMENTS_ENDPOINTS.DETAIL(documentId));

  const updateDocument = (documentId: string, payload: UpdateDocumentPayload) =>
    apiClient.put<Document>(DOCUMENTS_ENDPOINTS.DETAIL(documentId), payload);

  const deleteDocument = (documentId: string) =>
    apiClient.delete<void>(DOCUMENTS_ENDPOINTS.DETAIL(documentId));

  /** Identité de chapitre : seules les clés présentes sont écrites, null efface. 409 si `chapter_ref` est pris. */
  const patchDocumentChapter = (documentId: string, patch: DocumentChapterPatch) =>
    apiClient.patch<Document>(DOCUMENTS_ENDPOINTS.CHAPTER(documentId), patch);

  const getLatestDocumentVersion = (documentId: string) =>
    apiClient.get<DocumentVersion>(DOCUMENTS_ENDPOINTS.LATEST_VERSION(documentId));

  /**
   * Télécharge le Markdown ou le manifeste de la dernière version d'un
   * chapitre. Le texte d'un chapitre est en base (pas de fichier stocké) :
   * on le lit par l'API, puis on le donne au navigateur comme un fichier.
   */
  const downloadChapterSource = async (
    doc: Pick<Document, "id" | "chapter_ref" | "title">,
    part: ChapterSourcePart = "markdown",
  ) => {
    const { data } = await apiClient.get<Blob>(DOCUMENTS_ENDPOINTS.CHAPTER_SOURCE(doc.id), {
      params: { part },
      responseType: "blob",
    });
    const base = doc.chapter_ref || doc.title || doc.id;
    const url = URL.createObjectURL(data);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = part === "manifest" ? `${base}.manifest.json` : `${base}.md`;
    window.document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  /** Texte Markdown de la dernière version d'un chapitre (lecture à l'écran). */
  const getChapterMarkdown = async (documentId: string) => {
    const { data } = await apiClient.get<string>(DOCUMENTS_ENDPOINTS.CHAPTER_SOURCE(documentId), {
      params: { part: "markdown" },
      responseType: "text",
      transformResponse: (raw) => raw,
    });
    return data;
  };

  /** Relecture humaine : état, points à vérifier (dernière version), historique. */
  const getReview = (documentId: string) => apiClient.get<DocumentReview>(DOCUMENTS_ENDPOINTS.REVIEW(documentId));

  /** Décision du relecteur sur la dernière version ; rend l'état à jour. */
  const postReview = (documentId: string, payload: ReviewDecisionPayload) =>
    apiClient.post<DocumentReview>(DOCUMENTS_ENDPOINTS.REVIEW(documentId), payload);

  /** Ouvre / ferme le cours aux élèves ; rend aussi l'état de sa matière. */
  const setDocumentActivation = (documentId: string, isActive: boolean) =>
    apiClient.put<DocumentActivation>(DOCUMENTS_ENDPOINTS.ACTIVATION(documentId), { is_active: isActive });

  return {
    listDocuments,
    setDocumentActivation,
    getChapterMarkdown,
    getReview,
    postReview,
    getDocument,
    updateDocument,
    deleteDocument,
    patchDocumentChapter,
    getLatestDocumentVersion,
    downloadChapterSource,
  };
};
